import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UserPlus, GraduationCap, BookOpen, Settings2, Wallet, Landmark, Percent, ClipboardCheck } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { hasAcademyAccess } from "@/lib/academyAccess";
import { money, editionLabel, LEAD_STAGES } from "@/lib/academyFormat";
import { ACADEMY } from "@/constants/testIds";

const TILES = [
  { to: "/academia/interesados", label: "Interesados", text: "Cada interesado con su etapa, sus notas y su WhatsApp.", icon: UserPlus, perms: ["executive"] },
  { to: "/academia/alumnos", label: "Alumnos", text: "Inscripciones, cuenta de cada alumno y pagos.", icon: GraduationCap, perms: [] },
  { to: "/academia/cursos", label: "Cursos y fechas", text: "Las certificaciones, sus fechas, cupos y precios.", icon: BookOpen, perms: [] },
  // Fix D (2026-10-18)
  { to: "/academia/practicas", label: "Prácticas", text: "Las prácticas agendadas del día y quién asistió.", icon: ClipboardCheck, perms: ["reception", "academic", "instructor"] },
  { to: "/academia/caja", label: "Caja", text: "Abrir, cobrar, contar billetes y cerrar.", icon: Wallet, perms: ["reception", "finance"] },
  { to: "/academia/finanzas", label: "Finanzas", text: "Ingresos, gastos, reembolsos y balance del mes.", icon: Landmark, perms: ["finance"] },
  // Fix X (2026-10-13)
  { to: "/academia/comisiones", label: "Comisiones", text: "Lo cobrado de cada ejecutivo, su comisión y lo de los instructores.", icon: Percent, perms: ["executive", "finance", "instructor"] },
  { to: "/academia/configuracion", label: "Configuración", text: "Reserva, comisiones, formulario y contrato.", icon: Settings2, perms: ["finance"] },
];

// Fix U (2026-10-13): entrada de la academia: accesos segun permisos y proximas fechas.
export default function AcademyHomePage() {
  const { user } = useAuth();
  const [editions, setEditions] = useState([]);
  const [leads, setLeads] = useState(null);
  const isExecutive = hasAcademyAccess(user, ["executive"]);

  useEffect(() => {
    api.get("/academy/editions", { params: { upcoming: true } }).then(({ data }) => setEditions(data.slice(0, 8))).catch(() => setEditions([]));
    if (isExecutive) api.get("/academy/leads").then(({ data }) => setLeads(data)).catch(() => setLeads(null));
  }, [isExecutive]);

  const tiles = TILES.filter((t) => hasAcademyAccess(user, t.perms));
  return (
    <div className="space-y-8" data-testid={ACADEMY.homePage}>
      <div>
        <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-foreground">Academia</h1>
        <p className="text-base text-muted-foreground mt-1">AAME Academy: interesados, alumnos, cursos y su plata, en un solo lugar.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {tiles.map((t) => (
          <Link key={t.to} to={t.to} className="bg-white border border-border rounded-xl p-5 hover:border-brand transition-colors" data-testid={`${ACADEMY.homeTilePrefix}${t.to.split("/").pop()}`}>
            <t.icon className="w-6 h-6 text-brand mb-3" />
            <p className="font-medium text-foreground">{t.label}</p>
            <p className="text-sm text-muted-foreground mt-1">{t.text}</p>
          </Link>
        ))}
      </div>

      {leads && (
        <div className="bg-white border border-border rounded-xl p-5" data-testid={ACADEMY.homeLeadsSummary}>
          <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground mb-3">{user?.role === "admin" ? "Interesados" : "Mis interesados"}</p>
          <div className="flex flex-wrap gap-3">
            {LEAD_STAGES.map((s) => (
              <Link key={s.key} to={`/academia/interesados?etapa=${s.key}`} className={`px-3 py-2 rounded-lg text-sm ${s.cls}`}>
                {s.label}: <strong>{leads.filter((l) => l.stage === s.key).length}</strong>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-border rounded-xl overflow-x-auto">
        <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground px-5 pt-5 pb-3">Próximas fechas</p>
        {editions.length === 0 ? (
          <p className="text-sm text-muted-foreground px-5 pb-5">No hay fechas cargadas.</p>
        ) : (
          <table className="w-full text-sm" data-testid={ACADEMY.homeEditionsTable}>
            <thead>
              <tr className="text-xs uppercase tracking-[0.1em] text-muted-foreground bg-[#E9F3F3]">
                <th className="text-left font-medium py-2 px-5">Curso</th>
                <th className="text-left font-medium py-2 px-3">Fecha</th>
                <th className="text-left font-medium py-2 px-3">Lugares</th>
                <th className="text-left font-medium py-2 px-3">Precio hoy</th>
              </tr>
            </thead>
            <tbody>
              {editions.map((e) => (
                <tr key={e.id} className="border-t border-border">
                  <td className="py-2 px-5 font-medium">{e.course_name}</td>
                  <td className="py-2 px-3 text-muted-foreground">{editionLabel(e)}</td>
                  <td className="py-2 px-3">{e.capacity ? `${e.seats_taken} de ${e.capacity}` : `${e.seats_taken} (sin límite)`}</td>
                  <td className="py-2 px-3">{money(e.price_today.price)} <span className="text-xs text-muted-foreground">{e.price_today.label}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
